import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLayerGroup, faListCheck, faFileArrowUp,
  faArrowRight, faLock, faChartPie,
} from '@fortawesome/free-solid-svg-icons';
import { FadeUp } from '../components/common/FadeUp';
import { GlassCard } from '../components/common/GlassCard';
import { Button } from '../components/common/Button';
import { SocialProof, LiveUsersBadge } from '../components/common/SocialProof';
import { HorizontalScroll } from '../components/common/HorizontalScroll';
import { Link } from 'react-router-dom';

const FEATURES = [
  {
    icon: faLayerGroup,
    title: '템플릿 라이브러리',
    description: '신규, 갱신, 파트너 딜 유형별 MAP 템플릿을 제공합니다. 팀 워크플로우에 맞게 커스텀하세요.',
    color: 'var(--color-accent)',
    span: 'span-2',
  },
  {
    icon: faListCheck,
    title: '태스크 관리',
    description: '태스크를 생성하고, 담당자를 배정하고, 실시간으로 진행 상황을 추적하세요.',
    color: 'var(--color-success)',
    span: 'span-1',
  },
  {
    icon: faFileArrowUp,
    title: '문서 공유',
    description: '열람 추적이 가능한 안전한 문서 공유. PDF, DOCX, PPTX 등 다양한 형식을 지원합니다.',
    color: 'var(--color-info)',
    span: 'span-1',
  },
  {
    icon: faLock,
    title: '엔터프라이즈 보안',
    description: 'SOC 2 Type II 준비 완료. GDPR 및 개인정보보호법을 준수합니다.',
    color: 'var(--color-warning)',
    span: 'span-1',
  },
  {
    icon: faChartPie,
    title: '분석 및 인사이트',
    description: '문서 열람, 딜 진행률, 팀 성과를 실시간으로 모니터링하세요.',
    color: 'var(--color-error)',
    span: 'span-1',
  },
];

const WORKFLOW_STEPS = [
  { icon: faLayerGroup, title: '템플릿 선택', desc: '딜 유형에 맞는 MAP 템플릿을 선택하세요' },
  { icon: faListCheck, title: '태스크 배정', desc: '팀원에게 업무를 분배하세요' },
  { icon: faFileArrowUp, title: '문서 공유', desc: '고객에게 안전하게 문서를 공유하세요' },
  { icon: faChartPie, title: '진행 추적', desc: '딜 상태를 실시간으로 모니터링하세요' },
];

export function Dashboard() {
  return (
    <main id="main-content" role="main">
      {/* Hero Section */}
      <section
        aria-labelledby="hero-heading"
        className="container"
        style={{ padding: 'var(--space-16) var(--space-4) var(--space-12)' }}
      >
        <div className="hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 'var(--space-10)', alignItems: 'center' }}>
          <FadeUp>
            <div>
              <LiveUsersBadge />
              <h1
                id="hero-heading"
                style={{
                  fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                  fontWeight: 800,
                  marginTop: 'var(--space-6)',
                  marginBottom: 'var(--space-5)',
                  lineHeight: 1.2,
                  maxWidth: '560px',
                }}
              >
                고객이 진짜 쓰고 싶은
                <br />
                <span style={{ color: 'var(--color-accent)' }}>딜룸</span>을 만드세요
              </h1>
              <p
                style={{
                  fontSize: 'var(--text-lg)',
                  color: 'var(--color-text-secondary)',
                  maxWidth: '480px',
                  marginBottom: 'var(--space-8)',
                  lineHeight: 'var(--leading-relaxed)',
                }}
              >
                제안서, 계약서, 체크리스트를 하나의 공간에서.
                고객은 진행 상황을 확인하고, 팀은 더 빠르게 클로징합니다.
              </p>
              <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                <Link to="/login">
                  <Button variant="primary" size="lg" icon={<FontAwesomeIcon icon={faArrowRight} />}>
                    무료로 시작하기
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button variant="secondary" size="lg">
                    요금제 보기
                  </Button>
                </Link>
              </div>
            </div>
          </FadeUp>

          {/* Product visual anchor */}
          <FadeUp delay={0.15}>
            <div
              style={{
                background: 'var(--color-surface-glass)',
                border: '1px solid var(--color-surface-glass-border)',
                borderRadius: 'var(--radius-2xl)',
                padding: 'var(--space-6)',
                backdropFilter: 'blur(var(--glass-blur))',
                boxShadow: 'var(--shadow-lg)',
                aspectRatio: '16/10',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
              }}
            >
              <div style={{ display: 'flex', gap: '2px', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                {['탐색', '제안', '협상', '성사'].map((stage, i) => (
                  <div key={stage} style={{
                    flex: 1, padding: 'var(--space-2)', textAlign: 'center',
                    fontSize: '10px', fontWeight: 600, fontFamily: 'var(--font-display)',
                    background: i < 2 ? 'var(--color-accent)' : i === 2 ? 'var(--color-accent-subtle)' : 'var(--color-bg-secondary)',
                    color: i < 2 ? 'white' : i === 2 ? 'var(--color-accent)' : 'var(--color-text-tertiary)',
                  }}>
                    {stage}
                  </div>
                ))}
              </div>
              {['Acme Corp 제안서 발송', '기술 검토 미팅 일정 잡기', '보안 질의서 작성'].map((task, i) => (
                <div key={task} style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
                  padding: 'var(--space-3)', borderRadius: 'var(--radius-md)',
                  background: 'var(--color-bg-secondary)', fontSize: 'var(--text-sm)',
                }}>
                  <div style={{
                    width: '18px', height: '18px', borderRadius: 'var(--radius-full)', flexShrink: 0,
                    border: i === 0 ? 'none' : '2px solid var(--color-border)',
                    background: i === 0 ? 'var(--color-success)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontSize: '10px',
                  }}>
                    {i === 0 && <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '8px', transform: 'rotate(-45deg)' }} />}
                  </div>
                  <span style={{ color: i === 0 ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)', textDecoration: i === 0 ? 'line-through' : 'none' }}>{task}</span>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 'var(--space-4)', marginTop: 'auto', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border-subtle)' }}>
                {[{ label: '진행 중 딜', value: '12' }, { label: '성사율', value: '68%' }, { label: '평균 클로징', value: '18일' }].map(s => (
                  <div key={s.label} style={{ flex: 1 }}>
                    <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, fontFamily: 'var(--font-display)' }}>{s.value}</div>
                    <div style={{ fontSize: '10px', color: 'var(--color-text-tertiary)' }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>

        <style>{`
          @media (min-width: 768px) {
            .hero-grid { grid-template-columns: 1fr 1fr !important; }
          }
        `}</style>
      </section>

      {/* Features */}
      <section
        aria-labelledby="features-heading"
        className="section-alt"
        style={{ padding: 'var(--space-16) 0' }}
      >
        <div className="container" style={{ padding: '0 var(--space-4)' }}>
        <FadeUp>
          <h2 id="features-heading" style={{ marginBottom: 'var(--space-3)' }}>
            딜룸이 제공하는 것
          </h2>
          <p style={{ marginBottom: 'var(--space-10)', color: 'var(--color-text-secondary)', maxWidth: '480px' }}>
            첫 미팅부터 계약 체결까지, 영업팀에게 필요한 모든 것을 제공합니다.
          </p>
        </FadeUp>

        <div className="bento-grid">
          {FEATURES.map((feature, i) => (
            <FadeUp key={feature.title} delay={i * 0.06}>
              <GlassCard className={feature.span} style={{ height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                  <FontAwesomeIcon icon={feature.icon} style={{ color: feature.color, fontSize: 'var(--text-base)' }} />
                  <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 600, fontFamily: 'var(--font-display)', margin: 0 }}>
                    {feature.title}
                  </h3>
                </div>
                <p style={{ fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                  {feature.description}
                </p>
              </GlassCard>
            </FadeUp>
          ))}
        </div>

        <style>{`
          .bento-grid { display: grid; grid-template-columns: 1fr; gap: var(--space-4); }
          @media (min-width: 768px) { .bento-grid { grid-template-columns: repeat(2, 1fr); } .bento-grid > :first-child { grid-column: span 2; } }
          @media (min-width: 1024px) { .bento-grid { grid-template-columns: repeat(3, 1fr); } .bento-grid > :first-child { grid-column: span 2; } }
        `}</style>
        </div>
      </section>

      {/* Workflow */}
      <section aria-labelledby="workflow-heading" className="container" style={{ padding: 'var(--space-16) var(--space-4)' }}>
        <FadeUp>
          <h2 id="workflow-heading" style={{ marginBottom: 'var(--space-3)' }}>
            4단계로 딜 클로징
          </h2>
          <p style={{ marginBottom: 'var(--space-10)', color: 'var(--color-text-secondary)', maxWidth: '400px' }}>
            템플릿 선택부터 계약 체결까지의 여정입니다.
          </p>
        </FadeUp>

        <div className="workflow-desktop" style={{ display: 'none' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)' }}>
            {WORKFLOW_STEPS.map((step, i) => (
              <FadeUp key={step.title} delay={i * 0.1}>
                <GlassCard style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-accent)', marginBottom: 'var(--space-3)', fontFamily: 'var(--font-display)' }}>
                    {i + 1}단계
                  </div>
                  <FontAwesomeIcon icon={step.icon} style={{ fontSize: 'var(--text-2xl)', color: 'var(--color-accent)', marginBottom: 'var(--space-3)' }} />
                  <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--space-2)' }}>{step.title}</h3>
                  <p style={{ fontSize: 'var(--text-sm)' }}>{step.desc}</p>
                  {i < WORKFLOW_STEPS.length - 1 && (
                    <FontAwesomeIcon icon={faArrowRight} style={{ position: 'absolute', right: '-1.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                  )}
                </GlassCard>
              </FadeUp>
            ))}
          </div>
        </div>

        <div className="workflow-mobile">
          <HorizontalScroll>
            {WORKFLOW_STEPS.map((step, i) => (
              <GlassCard key={step.title} style={{ minWidth: '240px', scrollSnapAlign: 'start', textAlign: 'center', flexShrink: 0 }}>
                <div style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: 'var(--color-accent)', marginBottom: 'var(--space-3)', fontFamily: 'var(--font-display)' }}>
                  {i + 1}단계
                </div>
                <FontAwesomeIcon icon={step.icon} style={{ fontSize: 'var(--text-2xl)', color: 'var(--color-accent)', marginBottom: 'var(--space-3)' }} />
                <h3 style={{ fontSize: 'var(--text-base)', marginBottom: 'var(--space-2)' }}>{step.title}</h3>
                <p style={{ fontSize: 'var(--text-sm)' }}>{step.desc}</p>
              </GlassCard>
            ))}
          </HorizontalScroll>
        </div>

        <style>{`
          .workflow-desktop { display: none; }
          .workflow-mobile { display: block; }
          @media (min-width: 768px) { .workflow-desktop { display: block !important; } .workflow-mobile { display: none !important; } }
        `}</style>
      </section>

      {/* Social Proof */}
      <div className="section-alt" style={{ padding: 'var(--space-4) 0' }}>
        <div className="container">
          <SocialProof />
        </div>
      </div>

      {/* CTA */}
      <section aria-labelledby="cta-heading" style={{ padding: 'var(--space-20) var(--space-4)', textAlign: 'center' }}>
        <FadeUp>
          <GlassCard style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center', padding: 'var(--space-12)' }}>
            <h2 id="cta-heading" style={{ marginBottom: 'var(--space-4)' }}>
              지금 시작할 준비 되셨나요?
            </h2>
            <p style={{ marginBottom: 'var(--space-8)', color: 'var(--color-text-secondary)' }}>
              오늘부터 딜을 더 효율적으로 관리하세요.
            </p>
            <Link to="/login">
              <Button variant="primary" size="lg" icon={<FontAwesomeIcon icon={faArrowRight} />}>
                무료 체험 시작
              </Button>
            </Link>
          </GlassCard>
        </FadeUp>
      </section>
    </main>
  );
}
